import { useRef, useState, useEffect, memo } from "react";

// Color bin interface
export interface ColorBin {
  min: number;
  max: number;
  color: string;
  label?: string;
}

// Props interface for the legend component
export interface MapLegendProps {
  propertyName: string;
  units?: string;
  bins: ColorBin[];
  position?: "top-left" | "top-right" | "bottom-left" | "bottom-right";
  title?: string;
  className?: string;
  collapsed?: boolean;
  onToggle?: () => void;
  loading?: boolean;
  overlapRef?: React.RefObject<HTMLElement>;
}

const ENTER_LEFT_BUFFER_PX = 16;
const EXIT_LEFT_BUFFER_PX = 64;

/**
 * MapLegend Component
 *
 * A collapsible legend component for displaying color-coded value ranges on a map.
 * When the legend overlaps with the element referenced by overlapRef (e.g. the
 * property selector), bins are rendered in a horizontal row instead of a column.
 */
const MapLegend = memo(
  ({
    propertyName,
    units,
    bins,
    position = "bottom-right",
    title = "Legend",
    className = "",
    collapsed: controlledCollapsed,
    onToggle: controlledOnToggle,
    loading = false,
    overlapRef,
  }: MapLegendProps) => {
    const legendRef = useRef<HTMLDivElement>(null);
    const [isHorizontal, setIsHorizontal] = useState(false);
    const naturalHeightRef = useRef<number>(0);

    const [internalCollapsed, setInternalCollapsed] = useState<boolean>(false);
    const currentLegendState = {
      propertyName,
      units,
      bins,
    };
    const committedLegendStateRef = useRef(currentLegendState);

    if (!loading) {
      committedLegendStateRef.current = currentLegendState;
    }

    const shouldPreserveLegend =
      loading && committedLegendStateRef.current.bins.length > 0;
    const legendState = shouldPreserveLegend
      ? committedLegendStateRef.current
      : currentLegendState;
    const displayPropertyName = legendState.propertyName;
    const displayUnits = legendState.units;
    const displayBins = legendState.bins;

    // Use controlled or uncontrolled collapsed state
    const collapsed =
      controlledCollapsed !== undefined
        ? controlledCollapsed
        : internalCollapsed;
    const onToggle =
      controlledOnToggle || (() => setInternalCollapsed(!internalCollapsed));

    useEffect(() => {
      if (!overlapRef) return;

      const checkOverlap = () => {
        const legendEl = legendRef.current;
        const otherEl = overlapRef.current;
        if (!legendEl || !otherEl) return;

        const legendRect = legendEl.getBoundingClientRect();
        const otherRect = otherEl.getBoundingClientRect();
        const containerRect = legendEl.parentElement?.getBoundingClientRect();

        if (
          (legendRect.width === 0 && legendRect.height === 0) ||
          !containerRect
        ) {
          return;
        }

        setIsHorizontal((prev) => {
          if (!prev && legendRect.height > 0) {
            naturalHeightRef.current = legendRect.height;
          }
          const heightToCheck = naturalHeightRef.current || legendRect.height;
          const defaultBottomOffset = window.matchMedia("(min-width: 640px)")
            .matches
            ? 16
            : 24;
          const simulatedDefaultTop =
            containerRect.bottom - defaultBottomOffset - heightToCheck;
          const clearance = simulatedDefaultTop - otherRect.bottom;

          if (prev) {
            return clearance < EXIT_LEFT_BUFFER_PX;
          }

          return clearance < ENTER_LEFT_BUFFER_PX;
        });
      };

      checkOverlap();

      const observer = new ResizeObserver(checkOverlap);
      if (legendRef.current) observer.observe(legendRef.current);
      if (overlapRef.current) observer.observe(overlapRef.current);

      const container = legendRef.current?.parentElement;
      if (container) observer.observe(container);

      window.addEventListener("resize", checkOverlap);
      return () => {
        observer.disconnect();
        window.removeEventListener("resize", checkOverlap);
      };
    }, [overlapRef]);

    // Don't render anything if there are no bins
    if (!displayBins || displayBins.length === 0) {
      return null;
    }

    // Light mode styles
    const themeStyles = {
      container: "bg-white/95 border-gray-200 text-gray-900",
      title: "text-gray-700",
      button: "text-gray-500 hover:text-gray-700",
      subtitle: "text-gray-600",
      binText: "text-gray-600",
      binBorder: "border-gray-300",
      divider: "border-gray-200",
    };

    // Get position styles
    // bottom positions use larger offset (bottom-14 = 56px) to stay above map attribution
    const positionStyles = {
      "top-left": "absolute top-2 left-2 z-10 sm:top-4 sm:left-4",
      "top-right": "absolute top-2 right-2 z-10 sm:top-4 sm:right-4",
      "bottom-left": "absolute bottom-10 left-2 z-10 sm:bottom-12 sm:left-4",
      "bottom-right": "absolute bottom-6 right-2 z-10 sm:bottom-4 sm:right-4",
    };

    // Keep the legend on the left while the default bottom-right placement is
    // tight. The overlap effect uses hysteresis so resize noise does not bounce
    // the legend between sides near the threshold.
    const activePositionStyle = isHorizontal
      ? positionStyles["bottom-left"]
      : positionStyles[position];

    const sortedBins = [...displayBins].sort((a, b) => a.min - b.min);

    return (
      <div
        ref={legendRef}
        className={`${activePositionStyle} ${themeStyles.container} max-w-[calc(100vw-1rem)] rounded-md border p-2 shadow-lg backdrop-blur-sm transition-shadow duration-300 animate-in fade-in zoom-in-95 sm:p-3 ${isHorizontal ? "w-[min(18rem,calc(100vw-1rem))] sm:w-[20rem] sm:translate-y-12" : "w-[min(12rem,calc(100vw-1rem))] sm:max-w-[12rem]"} ${className}`}
        aria-busy={loading}
      >
        <div
          className={`mb-1.5 flex items-center justify-between border-b sm:mb-2 ${themeStyles.divider}`}
        >
          <div className={`text-xs font-semibold ${themeStyles.title}`}>
            {title}
          </div>
          <button
            onClick={onToggle}
            className={`${themeStyles.button} flex cursor-pointer items-center gap-1 text-xs transition-colors`}
            aria-label={collapsed ? "Expand legend" : "Collapse legend"}
          >
            {collapsed ? "Show" : "Hide"}
            <span className="inline-block transform transition-transform">
              {collapsed ? "▼" : "▲"}
            </span>
          </button>
        </div>

        <div
          className={`overflow-hidden transition-all duration-300 ${collapsed ? "max-h-0 opacity-0" : "max-h-96 opacity-100"}`}
        >
          <div className={`mb-1 break-words text-xs font-medium ${themeStyles.subtitle}`}>
            {displayPropertyName} {displayUnits ? `(${displayUnits})` : ""}
          </div>

          {/* Sort bins by min value for consistent display */}
          {isHorizontal ? (
            // Overlap mode: fixed 3-column grid so columns align across rows
            <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 sm:grid-cols-3 sm:gap-x-3 sm:gap-y-2">
              {sortedBins.map((bin, index) => (
                <div
                  key={`${bin.min}-${bin.max}-${index}`}
                  className="flex min-w-0 items-center gap-1 text-[11px] sm:gap-1.5 sm:text-xs"
                >
                  <div
                    className={`h-3 w-3 flex-shrink-0 rounded-sm border sm:h-4 sm:w-4 ${themeStyles.binBorder}`}
                    style={{ backgroundColor: bin.color, opacity: 0.7 }}
                  />
                  <div className={`min-w-0 flex-1 ${themeStyles.binText}`}>
                    {bin.label ||
                      `${bin.min.toLocaleString()}-${bin.max.toLocaleString()}`}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {sortedBins.map((bin, index) => (
                <div key={index} className="flex min-w-0 items-center gap-1 text-[11px] sm:gap-1.5 sm:text-xs">
                  <div
                    className={`h-3 w-3 flex-shrink-0 rounded-sm border sm:h-4 sm:w-4 ${themeStyles.binBorder}`}
                    style={{ backgroundColor: bin.color, opacity: 0.7 }}
                  />
                  <div className={`min-w-0 break-words ${themeStyles.binText}`}>
                    {bin.label ||
                      `${bin.min.toLocaleString()}-${bin.max.toLocaleString()}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  },
);

// Add displayName for better debugging in React DevTools
MapLegend.displayName = "MapLegend";

export { MapLegend };
